import {
  WalletProviderPriority,
  getDefaultClient,
  getWalletProviderRegistry,
  hasExtension,
  registerExtension,
} from '@dynamic-labs-sdk/client/core';

import { registerEvmNetworkProviderBuilder } from '../../registerEvmNetworkProviderBuilder';
import type { AddBaseAccountEvmExtensionOptions } from '../baseAccount.types';
import { createBaseAccountEvmWalletProvider } from '../createBaseAccountEvmWalletProvider/createBaseAccountEvmWalletProvider';

export const BASE_ACCOUNT_EVM_EXTENSION_KEY = 'baseAccountEvm';

/**
 * Adds Base Account (Coinbase Smart Wallet + EOA via popup/QR) as an available
 * wallet provider for EVM. App metadata (name, logo) and supported network IDs
 * are read from the Dynamic client by default, so no per-call configuration is
 * required for the basic case.
 *
 * The Coinbase Wallet **browser extension** path is already handled by
 * `addEIP6963Extension` and `addEvmWindowInjectedExtension`. This extension
 * fills the gap for users without an injected provider — Smart Wallet popups,
 * mobile EOA via QR/deeplink, and any platform where `window.ethereum` is
 * unavailable.
 *
 * Once installed, apps connect via the generic public lifecycle:
 * `connectWithWalletProvider`, `verifyWalletAccount`,
 * `connectAndVerifyWithWalletProvider`.
 *
 * @param [options] - Optional preference and network ID overrides.
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @not-instrumented
 */
export const addBaseAccountEvmExtension = (
  options: AddBaseAccountEvmExtensionOptions = {},
  client = getDefaultClient()
): void => {
  if (hasExtension({ extensionKey: BASE_ACCOUNT_EVM_EXTENSION_KEY }, client)) {
    return;
  }

  registerExtension(
    { extensionKey: BASE_ACCOUNT_EVM_EXTENSION_KEY },
    client
  );
  registerEvmNetworkProviderBuilder(client);

  const walletProvider = createBaseAccountEvmWalletProvider(options, client);

  getWalletProviderRegistry(client).register({
    priority: WalletProviderPriority.WALLET_SDK,
    walletProvider,
  });
};
